import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const decodeToken = (token) => {
  try {
    // JWT payload is the second part (base64 encoded)
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState(null);

  useEffect(() => {
    // Check if user is logged in on mount
    const storedToken = localStorage.getItem("adminToken");
    if (storedToken) {
      // Verify token with backend
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      await axios.get(`${API}/auth/verify`, {
        headers: {
          Authorization: `Bearer ${tokenToVerify}`,
        },
      });
      
      // Decode token to extract user info
      const decoded = decodeToken(tokenToVerify);
      if (decoded) {
        setUserRole(decoded.role || "admin"); // Default to admin for backwards compatibility
        setUsername(decoded.sub);
      }
      
      setToken(tokenToVerify);
      setIsAuthenticated(true);
    } catch (error) {
      // Token is invalid or expired
      localStorage.removeItem("adminToken");
      setToken(null);
      setUserRole(null);
      setUsername(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password, captchaId = null, captchaAnswer = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
        captcha_id: captchaId,
        captcha_answer: captchaAnswer,
      });
      
      const accessToken = response.data.access_token;
      
      // Decode token to extract user info
      const decoded = decodeToken(accessToken);
      if (decoded) {
        setUserRole(decoded.role || "admin"); // Default to admin for backwards compatibility
        setUsername(decoded.sub);
      }
      
      localStorage.setItem("adminToken", accessToken);
      setToken(accessToken);
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || "Login failed" 
      };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setToken(null);
    setUserRole(null);
    setUsername(null);
    localStorage.removeItem("adminToken");
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      login, 
      logout, 
      loading, 
      token,
      userRole,
      username,
      getAuthHeader 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
